import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

export type SPUploadFormLocs =
	| 'SPUploadFormContentInstructions'
	| 'SPUploadFormFieldErrorActiveDateRequired'
	| 'SPUploadFormFieldErrorActiveDateInvalid'
	| 'SPUploadFormFieldLabelOverrideExpireDate'
	| 'SPUploadFormFieldErrorExpireDateInvalid'
	| 'SPUploadFormFieldErrorProviderRequired'
	| 'SPUploadFormFieldErrorDocumentationSizeHint'
	| 'SPUploadFormFieldErrorDocumentationRequired'
	| 'SPUploadFormFieldErrorDocumentationSize'
	| 'SPUploadFormFieldErrorDocumentationLimit'
	| 'SPUploadFormFieldErrorPointsRequired'
	| 'SPUploadFormFieldErrorPointsNumeric'
	| 'SPUploadFormFieldLabelComments'
	| 'SPUploadFormFieldHintComments'
	| 'SPUploadFormButtonStepTwoBack'
	| 'SPUploadFormButtonStepTwoNext'
	| 'SPUploadFormFieldErrorPointsFactor'
	| 'SPCrcatPointsMax'
	| 'SPCrcatPointsMin';

export type CPUploadFormLocs =
	| 'CPUploadFormContentInstructions'
	| 'CPUploadFormFieldErrorActiveDateRequired'
	| 'CPUploadFormFieldErrorActiveDateInvalid'
	| 'CPUploadFormFieldErrorExpireDateInvalid'
	| 'CPUploadFormFieldErrorProviderRequired'
	| 'CPUploadFormFieldErrorDocumentationSizeHint'
	| 'CPUploadFormFieldErrorDocumentationRequired'
	| 'CPUploadFormFieldErrorDocumentationSize'
	| 'CPUploadFormFieldErrorDocumentationLimit'
	| 'CPUploadFormFieldErrorPointsRequired'
	| 'CPUploadFormFieldErrorPointsNumeric'
	| 'CPUploadFormButtonStepTwoBack'
	| 'CPUploadFormButtonStepTwoNext'
	| 'CPUploadFormFieldErrorPointsFactor'
	| 'CPCrcatPointsMax'
	| 'CPCrcatPointsMin';

export const spUploadFormLocs: LocDefs<SPUploadFormLocs> = {
	SPUploadFormContentInstructions: {
		defaultValue: '',
		description: 'Instructions for uploading an item',
		displayPath: 'History|Upload Items|Instructions',
		displayType: 'Content',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.Content.Instructions',
		htmlAllowed: true,
	},

	SPUploadFormFieldErrorActiveDateRequired: {
		defaultValue: 'This field is required',
		description: 'Error message when active date is required',
		displayPath: 'History|Upload Items|Active Date Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.ActiveDateRequired',
	},
	SPUploadFormFieldErrorActiveDateInvalid: {
		defaultValue: 'You must use a valid date',
		description: 'Error message when active date is invalid',
		displayPath: 'History|Upload Items|Active Date Invalid',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.ActiveDateInvalid',
	},
	SPUploadFormFieldLabelOverrideExpireDate: {
		defaultValue: 'Override Expiration',
		description: 'Label for the override expire date checkbox',
		displayPath: 'History|Upload Items|Override Expire Date',
		displayType: 'Label',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldLabel.OverrideExpireDate',
	},
	SPUploadFormFieldErrorExpireDateInvalid: {
		defaultValue: 'You must use a valid date',
		description: 'Error message when expire date is invalid',
		displayPath: 'History|Upload Items|Expire Date Invalid',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.ExpireDateInvalid',
	},
	SPUploadFormFieldErrorProviderRequired: {
		defaultValue: 'This field is required',
		description: 'Error message when provider is required',
		displayPath: 'History|Upload Items|Provider Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.ProviderRequired',
	},
	SPUploadFormFieldErrorDocumentationSizeHint: {
		defaultValue: 'Files must not be larger than 20Mb',
		description: 'Hint for documentation file size limit',
		displayPath: 'History|Upload Items|Documentation Size Hint',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.DocumentationSizeHint',
	},
	SPUploadFormFieldErrorDocumentationRequired: {
		defaultValue: 'This field is required',
		description: 'Error message when documentation is required',
		displayPath: 'History|Upload Items|Documentation Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.DocumentationRequired',
	},
	SPUploadFormFieldErrorDocumentationSize: {
		defaultValue: 'Files must not be larger than 2Mb',
		description: 'Error message when documentation file size is too large',
		displayPath: 'History|Upload Items|Documentation Size',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.DocumentationSize',
	},
	SPUploadFormFieldErrorDocumentationLimit: {
		defaultValue: 'Maximum of 5 files',
		description: 'Error message when documentation file limit is exceeded',
		displayPath: 'History|Upload Items|Documentation Limit',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldError.DocumentationLimit',
	},
	SPUploadFormFieldErrorPointsRequired: {
		defaultValue: ' are required',
		description: 'Error message when points are required',
		displayPath: 'History|Upload Items|Points Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'Credential.ContinuingEducation/Candidate.ProgramHistory.UploadItems.FieldError.PointsRequired',
	},
	SPUploadFormFieldErrorPointsNumeric: {
		defaultValue: 'must be a positive whole number.',
		description: 'Error message when points are not numeric',
		displayPath: 'History|Upload Items|Points Numeric',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'Credential.ContinuingEducation/Candidate.ProgramHistory.UploadItems.FieldError.PointsNumeric',
	},
	SPUploadFormFieldLabelComments: {
		defaultValue: 'Comments for Candidate History',
		description: 'Label for the comments field',
		displayPath: 'History|Upload Items|Comments',
		displayType: 'Label',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldLabel.Comments',
	},
	SPUploadFormFieldHintComments: {
		defaultValue: 'Not visible to candidates',
		description: 'Hint for the comments field',
		displayPath: 'History|Upload Items|Comments Hint',
		displayType: 'Content',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.FieldHint.Comments',
	},
	SPUploadFormButtonStepTwoBack: {
		defaultValue: 'Back',
		description: 'Text for the back button',
		displayPath: 'History|Upload Items|Back Button',
		displayType: 'Button',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.Button.StepTwoBack',
	},
	SPUploadFormButtonStepTwoNext: {
		defaultValue: 'Submit',
		description: 'Text for the submit button',
		displayPath: 'History|Upload Items|Submit Button',
		displayType: 'Button',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
		legacyName:
			'OtherRequirement.Upload/Candidates.ProgramHistory.UploadItems.Button.StepTwoNext',
	},
	SPUploadFormFieldErrorPointsFactor: {
		defaultValue: 'Points must be a multiple of',
		description: 'Error message when points are not a multiple of the factor',
		displayPath: 'History|Upload Items|Points Factor',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'SP',
	},
	SPCrcatPointsMax: {
		defaultValue: 'Points must be less than or equal to:',
		description: 'Hint/error message displayed when max points is set.',
		displayPath: `History|Upload Items|Max Points`,
		displayType: 'Error',
		feature: HubFeature.CredentialContinuingEducation,
		portal: 'SP',
	},
	SPCrcatPointsMin: {
		defaultValue: 'Points must be greater than or equal to:',
		displayPath: `History|Upload Items|Minimum Points`,
		description: `Hint/error message displayed when catalog points is set.`,
		displayType: 'Error',
		htmlAllowed: false,
		shared: false,
		feature: HubFeature.CredentialContinuingEducation,
		portal: 'SP',
	},
};

export const cpUploadFormLocs: LocDefs<CPUploadFormLocs> = {
	CPUploadFormContentInstructions: {
		defaultValue: '',
		description: 'Instructions for uploading an item',
		displayPath: 'History|Upload Items|Instructions',
		displayType: 'Content',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.Content.Instructions',
		htmlAllowed: true,
	},

	CPUploadFormFieldErrorActiveDateRequired: {
		defaultValue: 'This field is required',
		description: 'Error message when active date is required',
		displayPath: 'History|Upload Items|Active Date Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.ActiveDateRequired',
	},
	CPUploadFormFieldErrorActiveDateInvalid: {
		defaultValue: 'You must use a valid date',
		description: 'Error message when active date is invalid',
		displayPath: 'History|Upload Items|Active Date Invalid',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.ActiveDateInvalid',
	},
	CPUploadFormFieldErrorExpireDateInvalid: {
		defaultValue: 'You must use a valid date',
		description: 'Error message when expire date is invalid',
		displayPath: 'History|Upload Items|Expire Date Invalid',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.ExpireDateInvalid',
	},
	CPUploadFormFieldErrorProviderRequired: {
		defaultValue: 'This field is required',
		description: 'Error message when provider is required',
		displayPath: 'History|Upload Items|Provider Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.ProviderRequired',
	},
	CPUploadFormFieldErrorDocumentationSizeHint: {
		defaultValue: 'Files must not be larger than 20Mb',
		description: 'Hint for documentation file size limit',
		displayPath: 'History|Upload Items|Documentation Size Hint',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.DocumentationSizeHint',
	},
	CPUploadFormFieldErrorDocumentationRequired: {
		defaultValue: 'This field is required',
		description: 'Error message when documentation is required',
		displayPath: 'History|Upload Items|Documentation Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.DocumentationRequired',
	},
	CPUploadFormFieldErrorDocumentationSize: {
		defaultValue: 'Files must not be larger than 2Mb',
		description: 'Error message when documentation file size is too large',
		displayPath: 'History|Upload Items|Documentation Size',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.DocumentationSize',
	},
	CPUploadFormFieldErrorDocumentationLimit: {
		defaultValue: 'Maximum of 5 files',
		description: 'Error message when documentation file limit is exceeded',
		displayPath: 'History|Upload Items|Documentation Limit',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.FieldError.DocumentationLimit',
	},
	CPUploadFormFieldErrorPointsRequired: {
		defaultValue: ' are required',
		description: 'Error message when points are required',
		displayPath: 'History|Upload Items|Points Required',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'Credential.ContinuingEducation/Candidate.ProgramHistory.UploadItems.FieldError.PointsRequired',
	},
	CPUploadFormFieldErrorPointsNumeric: {
		defaultValue: 'must be a positive whole number.',
		description: 'Error message when points are not numeric',
		displayPath: 'History|Upload Items|Points Numeric',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'Credential.ContinuingEducation/Candidate.ProgramHistory.UploadItems.FieldError.PointsNumeric',
	},
	CPUploadFormButtonStepTwoBack: {
		defaultValue: 'Back',
		description: 'Text for the back button',
		displayPath: 'History|Upload Items|Back Button',
		displayType: 'Button',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.Button.StepTwoBack',
	},
	CPUploadFormButtonStepTwoNext: {
		defaultValue: 'Submit',
		description: 'Text for the submit button',
		displayPath: 'History|Upload Items|Submit Button',
		displayType: 'Button',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
		legacyName:
			'OtherRequirement.Upload.CandidateAccess/History.UploadItems.Button.StepTwoNext',
	},
	CPUploadFormFieldErrorPointsFactor: {
		defaultValue: 'Points must be a multiple of',
		description: 'Error message when points are not a multiple of the factor',
		displayPath: 'History|Upload Items|Points Factor',
		displayType: 'Error',
		feature: HubFeature.OtherRequirementUpload,
		portal: 'CP',
	},
	CPCrcatPointsMax: {
		defaultValue: 'Points must be less than or equal to:',
		description: 'Hint/error message displayed when max points is set.',
		displayPath: `Candidate|Program History|Upload Items|Max Points`,
		displayType: 'Error',
		feature: HubFeature.CredentialContinuingEducation,
		portal: 'CP',
	},
	CPCrcatPointsMin: {
		defaultValue: 'Points must be greater than or equal to:',
		displayPath: `Candidate|Program History|Upload Items|Minimum Points`,
		description: `Hint/error message displayed when catalog points is set.`,
		displayType: 'Error',
		htmlAllowed: false,
		shared: false,
		feature: HubFeature.CredentialContinuingEducation,
		portal: 'CP',
	},
};

export type CPUploadFormLocKeys = LocMap<CPUploadFormLocs>;
export const cpUploadFormLocKeys: CPUploadFormLocKeys =
	MapLocKeys<CPUploadFormLocs>(cpUploadFormLocs);

export type SPUploadFormLocKeys = LocMap<SPUploadFormLocs>;
export const spUploadFormLocKeys: SPUploadFormLocKeys =
	MapLocKeys<SPUploadFormLocs>(spUploadFormLocs);
